<template>
  <div
    :class="$style.modalContainer"
  >
    <StepHeader
      :class="$style.header"
      isVerifyStep
    />
    <ConfirmHead />
    <!-- TradeDesk pixel should not live here, refactor into it's own file
          or signupStoreConfig.js (this block was copied from SignUpConfirm.vue) -->
    <!-- TradeDesk Pixel -->
    <img
      alt=""
      :class="$style.pixel"
      height="1"
      src="https://insight.adsrvr.org/track/pxl/?adv=bgfmghe&ct=0:wndb6ja&fmt=3"
      style="border-style:none;"
      width="1"
    >
    <section :class="$style.content">
      <HeadingLarge
        :class="$style.heading"
        data-test-id="Confirm__Header"
      >
        <span v-if="codeAutofilled">
          Verifying Your Account...
        </span>
        <span v-else>
          Check Your Email!
        </span>
      </HeadingLarge>
      <template v-if="!codeAutofilled">
        <ParagraphBody
          bodySize="large"
        >
          We have sent an activation link to
          <strong :class="'fs-mask'"><a>{{ emailAddress }}</a></strong>.
          Please click the link provided in the email.
        </ParagraphBody>
        <ParagraphBody
          bodySize="large"
        >
          The link will expire in <strong>24 hours</strong>.
        </ParagraphBody>

        <figure :class="$style.illustrationContainer">
          <ConfirmationIllustrationCodeImg
            v-if="codeAutofilled"
            :class="$style.illustration"
          />
          <ConfirmationIllustrationImg
            v-else
            :class="$style.illustration"
          />
        </figure>
      </template>
      <!-- Not currectly displayed, but keeping logic bc design might return -->
      <ConfirmationCodeInput
        v-model="code"
        :class="$style.confirmationCodeContainer"
        :hasFailed="hasFailed"
        :onSubmit="delaySubmitConfirmRegistration"
        :resetFailedState="() => hasFailed = false"
      />
      <div :class="$style.feedbackContainer">
        <div
          v-if="!feedback.text"
        >
          <Button
            :class="$style.feedbackButton"
            hasOutline
            isFullWidth
            isMarketingStyle
            text="Having Trouble?"
            @click="toggleResendEmailInstructions"
          />
          <TransitionWrapper name="fade">
            <div
              v-if="resendEmailInstructionsVisible"
              :class="$style.resendInstructions"
            >
              <ParagraphBody>Did you check your spam folder?</ParagraphBody>
              <ParagraphBody>Is your email above correct?</ParagraphBody>
              <ParagraphBody>
                Still no idea?
                <Button
                  hasNoBackground
                  text="resend email"
                  @click="resendConfirmation"
                />
              </ParagraphBody>
            </div>
          </TransitionWrapper>
        </div>
        <TransitionWrapper name="fade">
          <div>
            <ParagraphBody
              v-if="feedback.text"
              :class="{ [$style.errorText]: feedback.isTextRed }"
            >
              <LoadingIcon
                v-if="feedback.loading"
                :class="$style.loadingIcon"
                inline
              />
              <Icon
                v-if="feedback.error"
                icon="error"
                inline
              />
              {{ feedback.text }}
            </ParagraphBody>
            <Button
              v-if="feedback.linkText"
              hasOutline
              isMarketingStyle
              :text="feedback.linkText"
              @click="resendConfirmation"
            />
            <ParagraphBody
              v-if="needsSupport"
              :class="$style.help"
            >
              Need some help?
              <Link
                external
                href="https://jumpcloud.com/support"
                text="Contact support"
              />
            </ParagraphBody>
          </div>
        </TransitionWrapper>
      </div>
    </section>
    <Footer
      :class="$style.footer"
    />
  </div>
</template>

<script>
/* eslint no-param-reassign: 0, import/no-cycle: 0 */
import {
  Button,
  HeadingLarge,
  Icon,
  Link,
  LoadingIcon,
  ParagraphBody,
  TransitionWrapper,
} from '@jumpcloud/ui-components';
import { email as emailRule } from 'vee-validate/dist/rules.umd';
import { extend, validate } from 'vee-validate';
import { mapActions, mapGetters } from 'vuex';
import ConfirmHead from 'client/signup/components/ConfirmHead.vue';
import ConfirmationCodeInput from 'client/signup/components/ConfirmationCodeInput.vue';
import ConfirmationIllustrationCodeImg from 'img/signup/confirmation-illustration-code.svg';
import ConfirmationIllustrationImg from 'img/signup/confirmation-illustration.svg';
import EventHelper from 'client/GrowthEngineering/EventHelper';
import Footer from 'client/signup/components/Footer.vue';
import SignupApi from 'client/signup/api/SignupApi';
import SignupConfirmationMessages from 'client/signup/SignupConfirmationMessages';
import StepHeader from 'client/signup/components/StepHeader.vue';

extend('emailParam', {
  ...emailRule,
  message: 'The email provided is invalid. Please try registering again.',
});

export default {
  name: 'Confirm',

  components: {
    Button,
    ConfirmHead,
    ConfirmationCodeInput,
    ConfirmationIllustrationCodeImg,
    ConfirmationIllustrationImg,
    Footer,
    HeadingLarge,
    Icon,
    Link,
    LoadingIcon,
    ParagraphBody,
    StepHeader,
    TransitionWrapper,
  },

  data: () => ({
    alreadyConfirmed: false,
    code: '',
    codeAutofilled: false,
    emailAddress: '',
    feedback: {
      error: false,
      isTextRed: false,
      loading: false,
      showResendEmailLink: false,
      text: '',
      linkText: '',
    },
    hasFailed: false,
    hasInvalidEmail: false,
    needsSupport: false,
    resendEmailInstructionsVisible: false,
  }),

  computed: {
    ...mapGetters('FeatureFlagsModule', [
      'betaFeatures',
    ]),

  },

  created() {
    this.getQueries();
    this.fetchFeatureFlags();
  },

  mounted() {
    // Auto-verify if the code has been provided in the query string
    if (this.code.length === 6) this.delaySubmitConfirmRegistration();
    this.setValidation(true);
  },

  methods: {
    ...mapActions([
      'confirmRegistration',
      'setValidation',
    ]),

    ...mapActions('FeatureFlagsModule', [
      'fetchFeatureFlags',
    ]),

    delaySubmitConfirmRegistration() {
      this.updateFeedbackText({
        loading: true,
        text: SignupConfirmationMessages.verifying,
      });
      // Use an artificial wait for UX and to prevent an issue where the page refreshes immediately
      // which causes the code to be submitted twice which, after the refresh, will show an error
      // even though the code worked the first time and the admin  is now verified
      // Resend should work for registed users but send an account recovery email, not a new code
      setTimeout(() => this.submitConfirmRegistration(), 3000);
    },

    getQueries() {
      const { query } = this.$route;
      // The email address should be passed in the query
      // string and is needed to confirm registration
      validate(query.email, 'emailParam').then((result) => {
        if (result.valid) {
          this.emailAddress = query.email;
        } else {
          this.hasInvalidEmail = true;

          this.updateFeedbackText({
            error: true,
            isTextRed: true,
            text: result.errors[0],
          });
        }
      });

      // The registration code _may_ be passed in and will autofill the input field if so
      if (query.registrationCode) {
        this.code = query.registrationCode;
        this.codeAutofilled = true;
      }
    },

    resendConfirmation() {
      if (this.feedback.error) {
        EventHelper.triggerResendEmailAfterError();
      } else {
        EventHelper.triggerResendEmailBeforeError();
      }

      this.updateFeedbackText({
        loading: true,
        text: SignupConfirmationMessages.resending,
      });

      return SignupApi.resendRegistrationCode(this.emailAddress)
        .then(() => (
          this.updateFeedbackText({ text: SignupConfirmationMessages.resent })
        ))
        .catch(() => {
          this.updateFeedbackText({
            isTextRed: true,
            text: SignupConfirmationMessages.resendError,
            linkText: SignupConfirmationMessages.tryAgain,
          });

          this.needsSupport = true;
        });
    },

    submitConfirmRegistration() {
      if (!this.hasInvalidEmail) {
        return this.confirmRegistration({ emailAddress: this.emailAddress, code: this.code })
          .catch((error) => {
            let feedbackText;
            let linkText;
            const showResendEmailLink = true;

            if (error && error.response && error.response.data.message.indexOf('Expired') >= 0) {
              feedbackText = SignupConfirmationMessages.expired;
              linkText = SignupConfirmationMessages.expiredLinkText;
            } else {
              feedbackText = SignupConfirmationMessages.invalid;
              linkText = SignupConfirmationMessages.invalidLinkText;
            }
            this.updateFeedbackText({
              error: true,
              isTextRed: true,
              showResendEmailLink,
              text: feedbackText,
              linkText,
            });
          });
      }

      return '';
    },

    toggleResendEmailInstructions() {
      if (!this.resendEmailInstructionsVisible) EventHelper.triggerVerificationHelpExpanded();
      this.resendEmailInstructionsVisible = !this.resendEmailInstructionsVisible;
    },

    updateFeedbackText(feedbackObj = {}) {
      this.feedback = {
        error: false,
        isTextRed: false,
        loading: false,
        showResendEmailLink: false,
        text: '',
        linkText: '',
        ...feedbackObj,
      };
      return this.feedback;
    },
  },
};
</script>

<style module scoped>
  .modalContainer {
    display: flex;
    flex-direction: column;
    height: 100%;
    justify-content: space-between;
    min-height: 42rem;
  }

  .pixel {
    position: absolute;
  }

  .banner {
    background-color: var(--jcTertiary);
    line-height: 1.5;
    padding: var(--jc-spacer);
    text-align: center;
  }

  .content {
    padding: var(--jc-spacer-medium) var(--jc-spacer) 0;
    text-align: center;
  }

  .heading {
    font-family: var(--serifFont);
  }

  .confirmationCodeContainer {
    align-items: center;
    display: none; /* Not deleting yet in case design returns */
    flex-wrap: wrap;
    justify-content: center;
    margin: var(--jc-spacer-medium) 0;
  }

  .resendInstructions {
    background: var(--jcWhite50);
    border: var(--jc-border-width) solid var(--jcWarning);
    border-radius: var(--jc-border-radius);
    margin-top: var(--jc-spacer-small);
    padding: var(--jc-spacer);
    position: absolute;
    width: 100%;
  }

  .feedbackContainer {
    margin: 0 auto var(--jc-spacer);
    position: relative;
    width: 18.5rem;
  }

  .errorText {
    color: var(--jcError);
  }

  .loadingIcon {
    color: var(--jcPrimary);
  }

  .illustrationContainer {
    align-items: center;
    display: flex;
    flex-direction: column;
    height: fit-content;
    justify-content: flex-end;
    margin: 0;
    padding: var(--jc-spacer-medium);
  }

  .illustration {
    align-self: center;
    height: auto;
    max-width: 20rem;
  }

  .help.help {
    margin-top: var(--jc-spacer);
  }

  .footer {
    padding: var(--jc-spacer-medium) 0;
  }
</style>
